import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  Select,
  Switch,
  Tooltip,
  notification,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { throttle } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Server } from '../../../../../api/server-index';
import { AuthContext } from '../../../../../context/auth-context';
import { UserDto } from '../../../../../types/dto/user.dto';
import { UserRoles } from '../../../../../types/enum/user-roles';
import { HasRoles } from '../../../../../utils/has-roles';
import { formItemLayout } from '../../../../layout/form/form-item.layout';

interface Props {
  viewedUser: UserDto;
  editUser: boolean;
  setReload: Dispatch<SetStateAction<boolean>>;
}

export function EditUser({ viewedUser, editUser, setReload }: Props) {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation(['user', 'common']);
  const [userForm] = useForm<UserDto>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isThrottled, setIsThrottled] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      userForm.setFieldsValue(viewedUser);
      userForm.setFieldValue('roles', viewedUser.roles);
      setLoading(false);
    };
    loadUser();
  }, [viewedUser, userForm]);

  const updateUser = async (updateUserValues: any) => {
    if (viewedUser) {
      try {
        setLoading(true);
        const responseUpdate = await Server.User.updateUser(updateUserValues);
        if (
          updateUserValues.roles?.length &&
          updateUserValues.roles?.length !== viewedUser?.roles?.length
        ) {
          await Server.User.updateUserRoles(
            viewedUser?.pin,
            updateUserValues.roles
          );
        }
        userForm.setFieldsValue(updateUserValues);

        if (responseUpdate.status === 200) {
          notification.success({ message: t('edit.success') });
        }
      } finally {
        setLoading(false);
        setReload(true);
      }
    }
  };

  const resendActivationLink = throttle(async () => {
    setIsThrottled(false);
    const response = await Server.User.resendActivationLink(viewedUser.pin);
    if (response.status === 200) {
      notification.success({ message: t('edit.activation_link_success') });
    }
  }, 180000);

  const handleResendClick = () => {
    if (isThrottled) {
      notification.warning({ message: t('edit.activation_link_limit') });
      return;
    }

    setIsThrottled(true);
    resendActivationLink();
  };

  return (
    <Card loading={loading} className='shadow-sm'>
      <Flex justify='space-between' align='center'>
        {viewedUser.isvuId ? (
          <Tooltip title={t('edit.tooltip')}>
            <InfoCircleOutlined />
          </Tooltip>
        ) : (
          <></>
        )}
        {HasRoles(
          [
            UserRoles.LECTURER,
            UserRoles.PROGRAMME_COORDINATOR,
            UserRoles.STAFF,
          ],
          viewedUser
        ) &&
        HasRoles(
          [UserRoles.ADMINISTRATOR, UserRoles.PROGRAMME_COORDINATOR],
          user
        ) ? (
          <Flex className='w-100' justify='end'>
            <Tooltip title={t('edit.activation_link_tooltip')}>
              <Button onClick={handleResendClick}>
                {t('edit.activation_link')}
              </Button>
            </Tooltip>
          </Flex>
        ) : (
          <></>
        )}
      </Flex>
      <Form
        className='mt-2'
        form={userForm}
        disabled={!editUser}
        onFinish={updateUser}
        {...formItemLayout}
      >
        <Form.Item name='pin' label={t('form.pin')}>
          <Input disabled />
        </Form.Item>
        <Form.Item name='hrEduUid' label={t('form.hrEduUid')}>
          <Input disabled />
        </Form.Item>
        <Form.Item name='givenName' label={t('form.givenName')}>
          <Input disabled={!!viewedUser.isvuId} />
        </Form.Item>
        <Form.Item name='familyName' label={t('form.familyName')}>
          <Input disabled={!!viewedUser.isvuId} />
        </Form.Item>
        <Form.Item name='email' label={t('form.email')}>
          <Input disabled={!!viewedUser.isvuId} />
        </Form.Item>
        <Form.Item name={['roles']} label={t('form.roles')}>
          <Select
            mode='multiple'
            allowClear
            options={Object.values(UserRoles).map((option) => {
              return {
                key: option,
                value: option,
                label: t(`roles.${option}`),
              };
            })}
          ></Select>
        </Form.Item>
        {viewedUser.roles?.length === 1 &&
        viewedUser.roles[0] === UserRoles.STUDENT ? (
          <></>
        ) : (
          <Form.Item name={'isEmployee'} label={t('form.isEmployee')}>
            <Switch value={!!viewedUser.isEmployee} />
          </Form.Item>
        )}
        <Flex justify='flex-end'>
          <Button type={'primary'} ghost onClick={userForm.submit}>
            {t('common:save')}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
}
